import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CalendarClock, CircleDollarSign, ClipboardCheck, ClipboardList, Clock, FileText, Flag, Fuel, Package, ShoppingCart, Wrench, type LucideIcon } from 'lucide-react';

import { formatarInteiro } from '@/lib/formato';
import { comCorpo, pedir } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';

import type { DadoDoAviso } from './explicacao';

/**
 * Os avisos da pessoa (0036): o que o sino da mesa e a tela de Avisos do
 * celular (M06) leem — o mesmo tipo, a mesma chave de cache e o mesmo icone por
 * assunto, para os dois nunca dizerem coisas diferentes do mesmo aviso.
 */
export interface Aviso {
  readonly id: string;
  readonly tipo: string;
  readonly titulo: string;
  readonly texto: string;
  readonly rota: string;
  readonly criadoEm: string;
  readonly equipamento: string | null;
  readonly lido: boolean;
  /** Desde a 0039. Nulo em aviso de antes dela. */
  readonly regra?: string | null;
  readonly periodoDe?: string | null;
  readonly periodoAte?: string | null;
  readonly dados?: readonly DadoDoAviso[];
}

export interface Explicacao {
  /** O registro da explicação (0039): é por ele que a pessoa diz se serviu (0076). */
  readonly id?: string;
  readonly sabe: boolean;
  readonly resposta: string;
  readonly falta: string | null;
}

export const CHAVE_DOS_AVISOS = ['avisos'] as const;

/** O assunto de cada tipo de aviso (0036): ícone do vocabulário e tom do bloco. */
export const ASSUNTO_DO_AVISO: Record<string, { readonly icone: LucideIcon; readonly bloco: string; readonly cor: string }> = {
  preventiva: { icone: Wrench, bloco: 'bg-erro-fundo', cor: 'text-erro' },
  ocorrencia_critica: { icone: ClipboardCheck, bloco: 'bg-erro-fundo', cor: 'text-erro' },
  consumo_fora_da_faixa: { icone: Fuel, bloco: 'bg-atencao-fundo', cor: 'text-atencao' },
  estoque_abaixo_do_minimo: { icone: Package, bloco: 'bg-atencao-fundo', cor: 'text-atencao' },
  os_aberta: { icone: ClipboardList, bloco: 'bg-informacao-fundo', cor: 'text-informacao' },
  documento_validado: { icone: FileText, bloco: 'bg-sucesso-fundo', cor: 'text-sucesso' },
  /* Pedido de correção ou de balanço esperando quem tem alçada (0065): pede
     decisão, e por isso é atenção, no ícone do combustível. */
  ajuste_pedido: { icone: Fuel, bloco: 'bg-atencao-fundo', cor: 'text-atencao' },
  /* A renovacao cobrada de quem responde pelo documento (0073): e para a
     pessoa, e pede acao — atencao, no icone do documento. */
  documento_cobrado: { icone: FileText, bloco: 'bg-atencao-fundo', cor: 'text-atencao' },
  /* O pedido de compra esperando quem tem alçada, e o devolvido para ajuste
     (0079): os dois pedem ação, no ícone de compras. */
  pedido_para_aprovar: { icone: ShoppingCart, bloco: 'bg-atencao-fundo', cor: 'text-atencao' },
  pedido_devolvido: { icone: ShoppingCart, bloco: 'bg-atencao-fundo', cor: 'text-atencao' },
  /* A divergência de horas aberta (0091): hora em disputa com o cliente,
     esperando quem confere — atenção, no ícone de operação e horas. */
  divergencia_de_horas: { icone: Clock, bloco: 'bg-atencao-fundo', cor: 'text-atencao' },
  /* O pedido que passou do prazo do fornecedor com item faltando (0091). */
  pedido_atrasado: { icone: ShoppingCart, bloco: 'bg-atencao-fundo', cor: 'text-atencao' },
  /* A troca com o ERP que passou da frequência da matriz (0104): é prazo, e
     pede ação — atenção, no ícone de prazo e vencimento. A divergência do
     retorno pede quem concilia — atenção, no ícone de apontar divergência. */
  troca_atrasada: { icone: CalendarClock, bloco: 'bg-atencao-fundo', cor: 'text-atencao' },
  divergencia_de_troca: { icone: Flag, bloco: 'bg-atencao-fundo', cor: 'text-atencao' },
  /* 0109: o desvio do orçamento sem explicação pede quem explica — atenção,
     no ícone de custos; a máquina candidata à renovação é marca para olhar
     (nível A1, não decide) — informativo, no ícone da marca do 20 (`flag`);
     o título do ERP vencido em aberto é prazo — atenção, no de vencimento. */
  desvio_de_orcamento: { icone: CircleDollarSign, bloco: 'bg-atencao-fundo', cor: 'text-atencao' },
  candidata_a_renovacao: { icone: Flag, bloco: 'bg-informacao-fundo', cor: 'text-informacao' },
  titulo_vencido: { icone: CalendarClock, bloco: 'bg-atencao-fundo', cor: 'text-atencao' },
  /* 0129: o lançamento feito sem sinal que o servidor recusou ao chegar — o
     litro saiu do tanque e não entrou no livro. Pede quem confere: erro, no
     ícone do combustível. */
  lancamento_do_campo_recusado: { icone: Fuel, bloco: 'bg-erro-fundo', cor: 'text-erro' },
};
export const ASSUNTO_SEM_TIPO = { icone: Bell, bloco: 'bg-tela', cor: 'text-tinta-secundaria' } as const;

/** A lista e a contagem de nao lidos. A cada minuto, como o sino. */
export function useAvisos() {
  return useQuery({
    queryKey: CHAVE_DOS_AVISOS,
    queryFn: () => pedir<{ avisos: readonly Aviso[]; naoLidos: number }>('/avisos'),
    refetchInterval: 60_000,
  });
}

/** Marcar lidos: os informados, ou todos. */
export function useMarcarLidos() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<{ marcados: number }, readonly string[] | undefined>((r, ids) => ({
        titulo: r.marcados === 1 ? '1 aviso marcado como lido' : `${formatarInteiro(r.marcados)} avisos marcados como lidos`,
        alvo: { nome: ids ? 'Avisos escolhidos' : 'Todos os avisos' },
        efeito: 'O contador do sino já desconta',
      })),
    },
    mutationFn: (ids?: readonly string[]) => comCorpo<{ marcados: number }>('POST', '/avisos/lidos', ids ? { ids } : {}),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DOS_AVISOS }),
  });
}
